import { useState, useEffect } from "react";
import AOS from 'aos';
import 'aos/dist/aos.css';
import './ViewCart.css';
import './Wishlist.css';
import { useSelector, useDispatch } from "react-redux";
import { removeProductFromCart, updateCartItemQuantity, clearUserCart } from "../redux/cartSlice";
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from "react-router-dom";
import { MdRemoveCircle } from "react-icons/md";
import { IoIosAddCircle } from "react-icons/io";
import { FaShoppingCart, FaShoppingBag, FaTag } from "react-icons/fa";
import { Modal, Button } from "react-bootstrap";
import ProductModal from '../components/ProductModal';
import styles from '../components/Card.module.css';
import { getProductById, apiFetch } from '../api';

const ViewCart = () => {
    // initialize AOS for animations
    const cart = useSelector(state => state.cart);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [itemToRemove, setItemToRemove] = useState(null);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    
    // Per-item coupon state - key is cartItemId
    const [itemCoupons, setItemCoupons] = useState({});
    const [couponInputs, setCouponInputs] = useState({});
    const [couponLoading, setCouponLoading] = useState({});
    const [couponErrors, setCouponErrors] = useState({});

    useEffect(() => {
        AOS.init({ duration: 1000, once: true });
    }, []);

    // Handle quantity change for cart items
    const handleQuantityChange = (product, newQuantity) => {
        // Find the current item in the cart matching the product and its selected options
        const currentItem = cart.items.find(item => item.id === product.id && item.selectedColor === product.selectedColor && item.selectedSize === product.selectedSize);
        if (!currentItem) return; 
        // If new quantity is less than 1, remove the item from cart
        if (newQuantity < 1) {
            dispatch(removeProductFromCart(currentItem)); 
        } else {
            dispatch(updateCartItemQuantity(currentItem, newQuantity));
        }
    };
    // Manage toast visibility state
    const [isToastVisible, setIsToastVisible] = useState(false);
    // Handle item removal with confirmation
    const handleRemoveItem = (item) => {
        setItemToRemove(item);
        setShowConfirm(true);
    };
    // Confirm item removal
    const handleConfirmRemove = async () => {
        if (itemToRemove) {
            // Dispatch action to remove item from cart
            try {
                // Remove item from cart
                await dispatch(removeProductFromCart(itemToRemove));
            } catch (err) {
                console.error(err);
                toast.error('Failed to remove item.');
            }
        }
        // Close confirmation modal and reset item to remove
        setShowConfirm(false);
        setItemToRemove(null);
    };
    // Handle clear cart with confirmation
    const handleClearCartClick = () => {
        setShowClearConfirm(true);
    };
    // handle confirm clear cart 
    const handleConfirmClearCart = async () => {
        if (isToastVisible) return;

        setShowClearConfirm(false);
        setIsToastVisible(true);
        try {
            // Clear the entire cart
            await dispatch(clearUserCart());
        } catch (err) {
            console.error(err);
            toast.error('Failed to clear cart.', { position: 'top-center', duration: 3000 });
        } finally {
            setIsToastVisible(false);
        }
    };
    // Group cart items by product ID
    const cartItems = cart?.items || [];
    // Group items by product ID to handle variants
    const groupedCartItems = cartItems.reduce((acc, item) => {
        const productId = item.id;
        // Initialize group if not exist
        if (!acc[productId]) {
            acc[productId] = {
                product: item,
                variants: []
            };
        }
        // Add item to the respective product group
        acc[productId].variants.push(item);
        return acc;
    }, {});

    // Helper function to safely format price
    const formatPrice = (price) => {
        // Convert price to number and format
        const numPrice = Number(price);
        return isNaN(numPrice) ? '₹0.00' : `₹${numPrice.toFixed(2)}`;
    };

    // Helper function to safely calculate subtotal
    const calculateSubtotal = (price, quantity) => {
        // calculate subtotal
        const numPrice = Number(price);
        const numQuantity = Number(quantity);
        return (isNaN(numPrice) || isNaN(numQuantity)) ? 0 : numPrice * numQuantity;
    };

    // Use totals directly from Redux state
    const displayTotal = cart.total || 0;
    const displayItemCount = cart.itemCount || 0;
    
    // Calculate item-level discounts and final total
    const calculateItemDiscount = (item, coupon) => {
        if (!coupon) return 0;
        const itemTotal = calculateSubtotal(item.price, item.quantity);
        
        if (coupon.discount_type === 'flat') {
            return Math.min(coupon.discount_value, itemTotal);
        } else if (coupon.discount_type === 'percent') {
            return itemTotal * coupon.discount_value / 100;
        }
        return 0;
    };
    
    // Calculate total discount from all applied coupons
    const totalDiscount = cartItems.reduce((sum, item) => {
        const itemKey = item.cartItemId || `${item.id}-${item.variantId || 0}`;
        const coupon = itemCoupons[itemKey];
        return sum + calculateItemDiscount(item, coupon);
    }, 0);
    
    const finalTotal = Math.max(0, displayTotal - totalDiscount);
    
    // Validate and apply coupon for specific item
    const handleApplyCoupon = async (item) => {
        const itemKey = item.cartItemId || `${item.id}-${item.variantId || 0}`;
        const couponCode = couponInputs[itemKey] || '';
        
        if (!couponCode.trim()) {
            toast.error('Please enter a coupon code');
            return;
        }

        setCouponLoading(prev => ({ ...prev, [itemKey]: true }));
        setCouponErrors(prev => ({ ...prev, [itemKey]: '' }));
        
        try {
            // Fetch all offers
            const response = await apiFetch('/api/offers/all');
            const offers = response.offers || response.data || response;
            
            const matchedOffer = offers.find(offer => 
                offer.couponcode && offer.couponcode.toLowerCase() === couponCode.toLowerCase()
            );
            
            if (!matchedOffer) {
                setCouponErrors(prev => ({ ...prev, [itemKey]: 'Invalid coupon code' }));
                toast.error('Invalid coupon code');
                return;
            }
            
            // Check expiry
            if (new Date(matchedOffer.expiry_date) < new Date()) {
                setCouponErrors(prev => ({ ...prev, [itemKey]: 'This coupon has expired' }));
                toast.error('This coupon has expired');
                return;
            }
            
            // Check minimum order amount for this item
            const itemTotal = calculateSubtotal(item.price, item.quantity);
            if (matchedOffer.min_order_amount && itemTotal < matchedOffer.min_order_amount) {
                setCouponErrors(prev => ({ ...prev, [itemKey]: `Min amount ₹${matchedOffer.min_order_amount} required` }));
                toast.error(`Minimum amount ₹${matchedOffer.min_order_amount} required for this item`);
                return;
            }
            
            // Check product-specific coupon
            if (matchedOffer.product) {
                const couponProductId = typeof matchedOffer.product === 'object' ? matchedOffer.product._id : matchedOffer.product;
                if (couponProductId !== item.id) {
                    setCouponErrors(prev => ({ ...prev, [itemKey]: 'Not applicable to this product' }));
                    toast.error('This coupon is not applicable to this product');
                    return;
                }
            }
            
            // Check category-specific coupon
            if (matchedOffer.category && !matchedOffer.product) {
                try {
                    const productData = await getProductById(item.id);
                    const product = productData.data || productData;
                    const couponCategoryId = typeof matchedOffer.category === 'object' ? matchedOffer.category._id : matchedOffer.category;
                    const productCategoryId = typeof product.category === 'object' ? product.category._id : product.category;
                    
                    if (couponCategoryId !== productCategoryId) {
                        setCouponErrors(prev => ({ ...prev, [itemKey]: 'Not applicable to this category' }));
                        toast.error('This coupon is not applicable to this product category');
                        return;
                    }
                } catch (err) {
                    console.error('Failed to fetch product category:', err);
                }
            }
            
            // Apply coupon to this item
            setItemCoupons(prev => ({ ...prev, [itemKey]: matchedOffer }));
            const discount = calculateItemDiscount(item, matchedOffer);
            toast.success(`Coupon applied! Saved ₹${discount.toFixed(2)} on this item`);
            
        } catch (error) {
            console.error('Error validating coupon:', error);
            setCouponErrors(prev => ({ ...prev, [itemKey]: 'Failed to validate' }));
            toast.error('Failed to validate coupon');
        } finally {
            setCouponLoading(prev => ({ ...prev, [itemKey]: false }));
        }
    };

    // Remove coupon from specific item
    const handleRemoveCoupon = (item) => {
        const itemKey = item.cartItemId || `${item.id}-${item.variantId || 0}`;
        setItemCoupons(prev => {
            const newCoupons = { ...prev };
            delete newCoupons[itemKey];
            return newCoupons;
        });
        setCouponInputs(prev => {
            const newInputs = { ...prev };
            delete newInputs[itemKey];
            return newInputs;
        });
        setCouponErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[itemKey];
            return newErrors;
        });
        toast.success('Coupon removed');
    };
    
    // If cart is empty, show empty cart message
    if (cartItems.length === 0) {
        return (
            <div className="wishlist-container">
                <div className="wishlist-header">
                    <div className="wishlist-title-section">
                        <div className="wishlist-icon-wrapper">
                            <FaShoppingCart className="wishlist-header-icon" />
                        </div>
                        <div>
                            <h1 className="wishlist-main-title">Shopping Cart</h1>
                            <p className="wishlist-subtitle">Your selected items</p>
                        </div>
                    </div>
                </div>
                
                {/* Empty page design */}
                <div className="wishlist-empty-state">
                    <div className="empty-state-icon-circle">
                        <FaShoppingCart className="empty-heart-icon" />
                    </div>
                    <h2 className="empty-state-title">Your Cart is Empty</h2>
                    <p className="empty-state-text">
                        Start adding items to your cart.
                        <br />They'll be saved here for you to checkout anytime!
                    </p>
                    <button 
                        className="btn-browse-products"
                        onClick={() => navigate('/products')}
                    >
                        <FaShoppingBag style={{ marginRight: '8px' }} />
                        Continue Shopping
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container my-5" data-aos="fade-up">
            <div className="row">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4" data-aos="fade-right">
                        <h2 className="mb-0">Shopping Cart</h2>
                        <button className="btn btn-outline-danger scale-btn clear-cart-sticky" onClick={handleClearCartClick} data-aos="fade-left" >
                            Clear Cart
                        </button>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-lg-8" data-aos="fade-up" data-aos-delay="100">
                    <div className="card" data-aos="zoom-in">
                        <div className="card-body">
                            {Object.values(groupedCartItems).map((group, groupIndex) => {
                                const isMultiVariant = group.variants.length > 1;
                                const firstVariant = group.variants[0];
                                
                                return (
                                <div key={`group-${firstVariant.id}-${groupIndex}`} style={{ marginBottom: '20px', borderBottom: '2px solid #f0f0f0', paddingBottom: '15px' }}>
                                    {/* Show product name once if multiple variants */}
                                    {isMultiVariant && (
                                        <div style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            padding: '10px 15px', 
                                            borderRadius: '8px',
                                            marginBottom: '10px',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <h6 style={{ margin: 0, color: '#82278A', fontWeight: '600', cursor: 'pointer' }} onClick={async () => {
                                                try {
                                                    const fullProduct = await getProductById(firstVariant.id);
                                                    setSelectedProduct(fullProduct.data);
                                                    setModalOpen(true);
                                                } catch (error) {
                                                    console.error('Failed to fetch product details:', error);
                                                }
                                            }}>
                                                📦 {firstVariant.title} 
                                                <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '10px' }}>
                                                    ({group.variants.length} variants)
                                                </span>
                                            </h6>
                                        </div>
                                    )}
                                    
                                    {/* Render each variant */}
                                    {group.variants.map((item, index) => (
                                <div key={item.cartItemId || `${item.id}-${item.variantId || index}`} className="row mb-4 border-bottom pb-3 cart-item-row" data-aos="fade-up" data-aos-delay={index * 200}>
                                    <div className="col-md-3 d-flex align-items-center">
                                        <img src={item.image} alt={item.title || 'Product Image'} className="cart-item-image" onClick={async () => {
                                            try {
                                                const fullProduct = await getProductById(item.id);
                                                setSelectedProduct(fullProduct.data);
                                                setModalOpen(true);
                                            } catch (error) {
                                                console.error('Failed to fetch product details:', error);
                                            }
                                        }} />
                                    </div>
                                    <div className="col-md-5 d-flex flex-column justify-content-center">
                                        <h5 className="card-title mb-2" style={{ cursor: "pointer", fontWeight: '600', color: '#333' }} onClick={async () => {
                                            try {
                                                const fullProduct = await getProductById(item.id);
                                                setSelectedProduct(fullProduct.data);
                                                setModalOpen(true);
                                            } catch (error) {
                                                console.error('Failed to fetch product details:', error);
                                            }
                                        }}>{item.title || 'Product'}</h5>
                                        
                                        {/* Variant Details */}
                                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', margin: '8px 0', fontSize: '0.95rem' }}>
                                            {item.selectedColor && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <strong style={{ color: "#82287A", minWidth: 'auto' }}>Color:</strong>
                                                    <span style={{
                                                        color: "#333",
                                                        backgroundColor: '#f0f0f0',
                                                        padding: '4px 10px',
                                                        borderRadius: '16px',
                                                        fontSize: '0.9rem',
                                                        fontWeight: '500'
                                                    }}>
                                                        {item.selectedColor}
                                                    </span>
                                                </div>
                                            )}
                                            {item.selectedSize && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <strong style={{ color: "#82287A", minWidth: 'auto' }}>Size:</strong>
                                                    <span style={{
                                                        color: "#333",
                                                        backgroundColor: '#f0f0f0',
                                                        padding: '4px 10px',
                                                        borderRadius: '16px',
                                                        fontSize: '0.9rem',
                                                        fontWeight: '500'
                                                    }}>
                                                        {item.selectedSize}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <p className="card-text text-muted" style={{ fontSize: "0.9rem", cursor: "pointer", marginTop: '8px' }} onClick={async () => {
                                            try {
                                                const fullProduct = await getProductById(item.id);
                                                setSelectedProduct(fullProduct.data);
                                                setModalOpen(true);
                                            } catch (error) {
                                                console.error('Failed to fetch product details:', error);
                                            }
                                        }}>
                                            {/* Description can be added here */}
                                        </p>
                                    </div>
                                    <div className="col-md-4 d-flex flex-column justify-content-center">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="h5 mb-0 text-brand">{formatPrice(item.price)}</span>
                                            <button className="btn btn-sm btn-outline-danger scale-btn" onClick={() => handleRemoveItem(item)}>
                                                Remove
                                            </button>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="me-2" style={{ marginBottom: "20px" }}>Quantity:</span>
                                            <div className={styles.quantityContainer}>
                                                <button className={styles.quantityButton} onClick={() => handleQuantityChange(item, (item.quantity || 1) - 1)} style={{ marginBottom: "20px" }}>
                                                    <MdRemoveCircle />
                                                </button>
                                                <span className={styles.quantity} style={{ marginBottom: "20px" }}>{item.quantity || 1}</span>
                                                <button className={styles.quantityButton} onClick={() => handleQuantityChange(item, (item.quantity || 0) + 1)} style={{ marginBottom: "20px" }}>
                                                    <IoIosAddCircle />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="mt-2">
                                            {(() => {
                                                const itemKey = item.cartItemId || `${item.id}-${item.variantId || 0}`;
                                                const appliedCoupon = itemCoupons[itemKey];
                                                const itemSubtotal = calculateSubtotal(item.price, item.quantity);
                                                const discount = calculateItemDiscount(item, appliedCoupon);
                                                const discountedPrice = itemSubtotal - discount;
                                                
                                                return (
                                                    <div>
                                                        {appliedCoupon ? (
                                                            <div style={{ marginBottom: '8px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                                    <span style={{ 
                                                                        textDecoration: 'line-through',
                                                                        color: '#dc3545',
                                                                        fontSize: '14px'
                                                                    }}>
                                                                        {formatPrice(itemSubtotal)}
                                                                    </span>
                                                                    <strong style={{ color: '#28a745', fontSize: '16px' }}>
                                                                        {formatPrice(discountedPrice)}
                                                                    </strong>
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                                    <div style={{
                                                                        display: 'inline-block',
                                                                        backgroundColor: '#28a745',
                                                                        color: 'white',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '11px',
                                                                        fontWeight: '600'
                                                                    }}>
                                                                        Saved ₹{discount.toFixed(2)}
                                                                    </div>
                                                                    <div style={{
                                                                        display: 'inline-block',
                                                                        backgroundColor: '#ff6b6b',
                                                                        color: 'white',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '11px',
                                                                        fontWeight: '600'
                                                                    }}>
                                                                        {((discount / itemSubtotal) * 100).toFixed(0)}% OFF
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <strong className="text-brand">Subtotal: {formatPrice(itemSubtotal)}</strong>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                    
                                    {/* Coupon section for this item */}
                                    <div className="col-12 mt-3">
                                        {(() => {
                                            const itemKey = item.cartItemId || `${item.id}-${item.variantId || 0}`;
                                            const appliedCoupon = itemCoupons[itemKey];
                                            const couponInput = couponInputs[itemKey] || '';
                                            const isLoading = couponLoading[itemKey];
                                            const error = couponErrors[itemKey];
                                            
                                            return (
                                                <div className="coupon-section">
                                                    <div className="coupon-header">
                                                        <FaTag className="coupon-tag" />
                                                        <span>Have a coupon for this item?</span>
                                                    </div>
                                                    {!appliedCoupon ? (
                                                        <div>
                                                            <div className="coupon-input-row">
                                                                <input
                                                                    type="text"
                                                                    placeholder="Enter coupon code"
                                                                    value={couponInput}
                                                                    onChange={(e) => {
                                                                        setCouponInputs(prev => ({ 
                                                                            ...prev, 
                                                                            [itemKey]: e.target.value.toUpperCase() 
                                                                        }));
                                                                        if (error) {
                                                                            setCouponErrors(prev => ({ ...prev, [itemKey]: '' }));
                                                                        }
                                                                    }}
                                                                    className={`coupon-input ${error ? 'has-error' : ''}`}
                                                                    onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon(item)}
                                                                />
                                                                <button
                                                                    onClick={() => handleApplyCoupon(item)}
                                                                    disabled={isLoading || !couponInput.trim()}
                                                                    className={`coupon-apply-btn ${isLoading || !couponInput.trim() ? 'disabled' : ''}`}
                                                                >
                                                                    {isLoading ? 'Applying...' : 'Apply'}
                                                                </button>
                                                            </div>
                                                            {/* No public coupon suggestions shown */}
                                                            {error && (
                                                                <div className="coupon-error">
                                                                    {error}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="coupon-applied">
                                                            <div>
                                                                <div className="applied-title">✓ Coupon applied</div>
                                                                <div className="applied-meta">
                                                                    {appliedCoupon.discount_type === 'percent' 
                                                                        ? `${appliedCoupon.discount_value}% discount` 
                                                                        : `₹${appliedCoupon.discount_value} off`}
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() => handleRemoveCoupon(item)}
                                                                className="coupon-remove-btn"
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            ))}
                                </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="col-lg-4 order-summary-sticky" data-aos="fade-up" data-aos-delay="200">
                    <div className="card" data-aos="zoom-in" data-aos-delay="300">
                        <div className="card-body">
                            <h5 className="card-title">Order Summary</h5>
                            <div className="d-flex justify-content-between mb-2">
                                
                                <span>Items ({displayItemCount})</span>
                                <span>{formatPrice(displayTotal)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Shipping</span>
                                <span>₹0.00</span>
                            </div>
                            
                            {totalDiscount > 0 && (
                                <div className="d-flex justify-content-between mb-2" style={{ color: '#28a745', fontWeight: '600' }}>
                                    <span>Coupon Discount ({((totalDiscount / displayTotal) * 100).toFixed(1)}% OFF)</span>
                                    <span>- ₹{totalDiscount.toFixed(2)}</span>
                                </div>
                            )}
                            
                            <hr />
                            <div className="d-flex justify-content-between mb-3">
                                <strong>Total</strong>
                                <div className="d-flex flex-column align-items-end">
                                    {totalDiscount > 0 && (
                                        <span style={{ 
                                            textDecoration: 'line-through', 
                                            color: '#dc3545',
                                            fontSize: '14px',
                                            marginBottom: '4px'
                                        }}>
                                            {formatPrice(displayTotal)}
                                        </span>
                                    )}
                                    <strong style={{ color: totalDiscount > 0 ? '#28a745' : '#000', fontSize: totalDiscount > 0 ? '20px' : '16px' }}>
                                        {formatPrice(finalTotal)}
                                    </strong>
                                </div>
                            </div>
                            {totalDiscount > 0 && (
                                <div style={{
                                    backgroundColor: '#d4edda',
                                    border: '1px solid #c3e6cb',
                                    borderRadius: '6px',
                                    padding: '8px 12px',
                                    marginBottom: '12px',
                                    fontSize: '13px',
                                    textAlign: 'center',
                                    color: '#155724',
                                    fontWeight: '600'
                                }}>
                                    🎉 You're saving ₹{totalDiscount.toFixed(2)} with coupons!
                                </div>
                            )}
                            <Link to="/checkout" state={{ itemCoupons, totalDiscount, finalTotal }} className="btn btn-primary w-100 mb-2 scale-btn" style={{ backgroundColor: "#82278A", borderColor: "#82278A" }} data-aos="zoom-in" data-aos-delay="400">
                                Proceed to Checkout
                            </Link>                            <Link to="/products" className="btn btn-outline-secondary w-100 scale-btn" data-aos="zoom-in" data-aos-delay="500">
                                Continue Shopping
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            {/* Product Modal */}
            <ProductModal post={selectedProduct} isOpen={modalOpen} onClose={() => setModalOpen(false)} initialSelectedColor={selectedProduct?.selectedColor} initialSelectedSize={selectedProduct?.selectedSize} isWishlistPage={false} />

            {/* Remove Item Confirmation Modal */}
            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: '2px solid #82278A' }}>
                    <Modal.Title style={{ color: '#82278A' }}>Confirm Removal</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ textAlign: 'center', padding: '30px', fontSize: '16px' }}>
                    Are you sure you want to remove this item from your cart?
                    {itemToRemove && (
                        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <strong>{itemToRemove.title}</strong>
                            {itemToRemove.selectedColor && itemToRemove.selectedSize && (
                                <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                                    {itemToRemove.selectedColor} / {itemToRemove.selectedSize}
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="confirm-actions" style={{ borderTop: 'none' }}>
                    <Button variant="secondary" onClick={() => setShowConfirm(false)} className="confirm-btn cancel-btn">
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmRemove} className="confirm-btn" style={{ backgroundColor: '#82278A', borderColor: '#82278A'}}>
                        Remove
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Clear Cart Confirmation Modal */}
            <Modal show={showClearConfirm} onHide={() => setShowClearConfirm(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: '2px solid #dc3545' }}>
                    <Modal.Title style={{ color: '#dc3545' }}>Clear Entire Cart?</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ textAlign: 'center', padding: '30px', fontSize: '16px' }}>
                    <p style={{ marginBottom: '15px' }}>Are you sure you want to clear all items from your cart?</p>
                    <p style={{ fontSize: '14px', color: '#666' }}>This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer className="confirm-actions" style={{ borderTop: 'none' }}>
                    <Button variant="secondary" onClick={() => setShowClearConfirm(false)} className="confirm-btn cancel-btn">
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmClearCart} className="confirm-btn">
                        Clear Cart
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ViewCart;